/**
 * Studio Helper hooks (Phase 4) — chat with the five sysadmin-published
 * Helper agents embedded in /studio/* + /agents/builder + /connections
 * surfaces.
 *
 * Day-3 (§3.3 SYSTEM-AGENT-HELPERS-DESIGN.md): tool-call → pendingMutations.
 *
 * Existing kinds:
 *   - tool_helper       → /studio/tools/new HelperPanel
 *   - skill_helper      → /studio/skills create flow
 *   - prompt_helper     → /studio/prompts create flow
 *
 * NEW (Day-1 endpoint, Day-2 mutation tools, Day-3 frontend):
 *   - architect         → /agents/builder
 *   - function_helper   → /connections/new-function ToolDeveloperPage
 *
 * Each kind ships its own mutation tool subset (server attaches them
 * to streamText). When the helper emits a tool call, the SSE stream
 * forwards it as `tool_call` (preview) + `tool_call_done` (server
 * confirmation). This hook parses those events and surfaces them as
 * `pendingMutations` for the Accept/Reject card stack.
 *
 * Uses the same SSE pattern as argus-chat. Q5: helpers only see
 * tenant catalog STRUCTURAL metadata (added server-side); they cannot
 * call tools or read documents — their tool calls only mutate the
 * caller's canvas.
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { apiClient } from './api-client';

// apiClient is referenced indirectly via the GET endpoint below.
void apiClient;

/**
 * Helper kind — re-exported from `@argusai/shared/types/helper-kind` so
 * the frontend shares ONE source of truth with the api package. Before
 * B-0 Foundation Wiring (2026-05-24) this was a local TS union that
 * omitted `ask_argus`. The CI gate at
 * `scripts/ci/helper-kind-drift-check.mjs` now fails the build if any
 * file re-declares this union locally.
 */
export type { HelperKind } from '@argusai/shared';
import type { HelperKind } from '@argusai/shared';

export type HelperScope = 'tenant' | 'sysadmin';

export interface HelperAgentRow {
  id: string;
  slug: string;
  displayName: string;
  description: string;
  kind: HelperKind;
  modelBrand: string;
  version: number;
}

export interface HelperMessage {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * One pending mutation parsed from a `tool_call` SSE event. Lives in
 * `pendingMutations` until the user clicks Accept (→ caller calls
 * `applyMutation`) or Reject (→ caller calls `rejectMutation`).
 *
 * `confirmed` becomes true when the matching `tool_call_done` event arrives.
 * `turnComplete` becomes true only after the stream emits `done`, so callers
 * cannot apply cards before final complete-brief validation runs.
 */
export interface PendingMutation {
  /** Stable id from the server (`callId`). One per tool call. */
  callId: string;
  /** Tool name — e.g. `set_identity`, `add_tool`, `set_code_body`. */
  toolName: string;
  /** Zod-validated args the model emitted. Frontend interprets these. */
  args: Record<string, unknown>;
  /** Set true when matching `tool_call_done` arrives. */
  confirmed: boolean;
  /** Set true after the assistant turn emits final `done`. */
  turnComplete: boolean;
  /** Index of the assistant message in `messages` that emitted this call. */
  messageIndex: number;
  /** ms timestamp (`performance.now`) — used for auto-reject + sort. */
  emittedAt: number;
  /**
   * Day-3 hallucinated-slug check: if the caller's catalog doesn't
   * recognise the slug (e.g. `add_tool({ slug: 'nope' })`), the Accept
   * button is disabled and `invalidReason` is shown on the card.
   * Caller populates this via `markMutationInvalid` after each tool
   * call arrives.
   */
  invalidReason?: string;
}

const KEYS = {
  agents: ['studio-helper-agents'] as const,
  agentsSysadmin: ['studio-helper-agents', 'sysadmin'] as const,
};

/**
 * #762 — when the caller is operating under a sysadmin route (e.g.
 * `/sysadmin/catalog/tools/new`), the localStorage token has audience
 * `argusai-admin` and the tenant route `/studio/helper/agents` returns
 * 401 INVALID_AUDIENCE. Use the sysadmin-audience alias under
 * `/admin/platform/system-catalog/helper-agents` (same shape) so the
 * shared `HelperPanel` keeps working without a noisy 401.
 */
export function useHelperAgents(scope: HelperScope = 'tenant') {
  const sysadmin = scope === 'sysadmin';
  return useQuery<{ items: HelperAgentRow[] }, Error>({
    queryKey: sysadmin ? KEYS.agentsSysadmin : KEYS.agents,
    queryFn: () => apiClient.get<{ items: HelperAgentRow[] }>(
      sysadmin
        ? '/admin/platform/system-catalog/helper-agents'
        : '/studio/helper/agents',
    ),
  });
}

export function useHelperAgent(kind: HelperKind | null, scope: HelperScope = 'tenant') {
  const q = useHelperAgents(scope);
  const found = q.data?.items.find((a) => a.kind === kind);
  return { ...q, agent: found ?? null };
}

interface UseHelperChatOptions {
  kind: HelperKind;
  /** Day-1: 'tenant' (default — back-compat) or 'sysadmin'. */
  scope?: HelperScope;
  /**
   * Day-1: current draft on the canvas. Sent on every request so the
   * helper can patch what exists rather than restart. Pass undefined
   * to fall back to chat-only mode (existing behaviour).
   */
  canvasState?: Record<string, unknown>;
}

interface UseHelperChatResult {
  messages: HelperMessage[];
  setMessages: (messages: HelperMessage[]) => void;
  isStreaming: boolean;
  send: (userMessage: string) => Promise<void>;
  reset: () => void;
  error: string | null;
  /** Day-3: tool-call mutations the helper proposed; awaiting Accept/Reject. */
  pendingMutations: PendingMutation[];
  /** Mark a mutation invalid (e.g. slug not in caller's catalog). */
  markMutationInvalid: (callId: string, reason: string) => void;
  /** Remove a single mutation from the pending stack (Accept or Reject). */
  consumeMutation: (callId: string) => void;
  /** Remove all pending mutations from the stack. */
  clearMutations: () => void;
}

/**
 * Streaming chat with a Helper agent. Reads SSE manually (the existing
 * apiClient doesn't expose a streaming primitive). Each `send` call
 * appends a user message + an assistant message that grows as tokens
 * arrive. `tool_call` events become entries in `pendingMutations`.
 */
export function useHelperChat(opts: UseHelperChatOptions): UseHelperChatResult {
  const [messages, setMessages] = useState<HelperMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingMutations, setPendingMutations] = useState<PendingMutation[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  // Caller-supplied canvasState changes between renders — keep a ref so
  // we don't tear down the `send` callback on every keystroke.
  const canvasStateRef = useRef<Record<string, unknown> | undefined>(opts.canvasState);
  canvasStateRef.current = opts.canvasState;

  const scope: HelperScope = opts.scope ?? 'tenant';

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setMessages([]);
    setIsStreaming(false);
    setError(null);
    setPendingMutations([]);
  }, []);

  const markMutationInvalid = useCallback((callId: string, reason: string) => {
    setPendingMutations((prev) =>
      prev.map((m) => (m.callId === callId ? { ...m, invalidReason: reason } : m)),
    );
  }, []);

  const consumeMutation = useCallback((callId: string) => {
    setPendingMutations((prev) => prev.filter((m) => m.callId !== callId));
  }, []);

  const clearMutations = useCallback(() => {
    setPendingMutations([]);
  }, []);

  const send = useCallback(async (userMessage: string) => {
    if (!userMessage.trim() || isStreaming) return;
    setError(null);
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    // Append the user turn + an empty assistant placeholder we mutate
    // as tokens stream in. Capture its index for tool_call attribution.
    let assistantIndex = -1;
    setMessages((prev) => {
      assistantIndex = prev.length + 1;
      return [
        ...prev,
        { role: 'user', content: userMessage },
        { role: 'assistant', content: '' },
      ];
    });
    setIsStreaming(true);

    try {
      const accessToken =
        typeof localStorage !== 'undefined'
          ? localStorage.getItem('accessToken')
          : null;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
      // #762-style sysadmin alias. When operating under a sysadmin Catalog
      // route the localStorage token has audience 'argusai-admin'. The tenant
      // chat endpoint /studio/helper/chat gates on tenant audience and returns
      // 401 INVALID_AUDIENCE for that token. Use the sysadmin-audience alias
      // (same handler, gated by requireSysadmin) so the sysadmin builders can
      // chat with their helper. Mirrors the helper-agents list alias.
      const chatUrl =
        scope === 'sysadmin'
          ? '/api/v1/admin/platform/system-catalog/helper-chat'
          : '/api/v1/studio/helper/chat';
      const res = await fetch(chatUrl, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          kind: opts.kind,
          scope,
          canvasState: canvasStateRef.current,
          messages: [
            ...messages,
            { role: 'user', content: userMessage },
          ],
        }),
        signal: ac.signal,
      });
      if (!res.ok || !res.body) {
        const text = await res.text().catch(() => '');
        throw new Error(`Helper chat failed: HTTP ${res.status} ${text.slice(0, 200)}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() ?? '';
        for (const evt of events) {
          if (!evt.startsWith('data: ')) continue;
          const json = evt.slice(6).trim();
          if (!json) continue;
          try {
            const payload = JSON.parse(json) as {
              type: string;
              content?: string;
              message?: string;
              code?: string;
              callId?: string;
              toolName?: string;
              args?: Record<string, unknown>;
              ok?: boolean;
            };

            if (payload.type === 'token' && typeof payload.content === 'string') {
              setMessages((prev) => {
                const next = [...prev];
                const last = next[next.length - 1];
                if (last && last.role === 'assistant') {
                  next[next.length - 1] = { ...last, content: last.content + payload.content };
                }
                return next;
              });
            } else if (
              payload.type === 'tool_call' &&
              typeof payload.callId === 'string' &&
              typeof payload.toolName === 'string' &&
              payload.args != null
            ) {
              // Day-3 §3.3 — surface as a pending mutation. The
              // Accept/Reject card stack renders the list; Accept
              // dispatches the matching canvas update and removes the
              // card via `consumeMutation`.
              setPendingMutations((prev) => [
                ...prev,
                {
                  callId: payload.callId!,
                  toolName: payload.toolName!,
                  args: payload.args!,
                  confirmed: false,
                  turnComplete: false,
                  messageIndex: assistantIndex,
                  emittedAt:
                    typeof performance !== 'undefined' ? performance.now() : Date.now(),
                },
              ]);
            } else if (
              payload.type === 'tool_call_done' &&
              typeof payload.callId === 'string'
            ) {
              setPendingMutations((prev) =>
                prev.map((m) =>
                  m.callId === payload.callId ? { ...m, confirmed: true } : m,
                ),
              );
            } else if (payload.type === 'done') {
              setPendingMutations((prev) =>
                prev.map((m) =>
                  m.messageIndex === assistantIndex ? { ...m, turnComplete: true } : m,
                ),
              );
            } else if (payload.type === 'error') {
              const message = `${payload.code ?? 'ERROR'}: ${payload.message ?? 'helper failed'}`;
              setError(message);
              if (payload.code === 'INCOMPLETE_HTTP_TOOL_BRIEF') {
                setPendingMutations((prev) =>
                  prev.map((m) =>
                    m.messageIndex === assistantIndex
                      ? { ...m, invalidReason: payload.message ?? 'Incomplete HTTP tool brief' }
                      : m,
                  ),
                );
              }
            }
            // metadata + done events are not visible to the consumer.
          } catch {
            // Ignore parse failures — log only at the dev console.
            // eslint-disable-next-line no-console
            console.warn('Helper chat: bad SSE payload', json);
          }
        }
      }
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return;
      setError((err as Error).message);
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last && last.role === 'assistant' && last.content === '') {
          next[next.length - 1] = { ...last, content: `_Helper failed: ${(err as Error).message}_` };
        }
        return next;
      });
    } finally {
      setIsStreaming(false);
    }
  }, [messages, isStreaming, opts.kind, scope]);

  return {
    messages,
    setMessages,
    isStreaming,
    send,
    reset,
    error,
    pendingMutations,
    markMutationInvalid,
    consumeMutation,
    clearMutations,
  };
}
